// apps/client/src/title/grade.ts (title polish, 2026-10-06) — the title's own grade over the clock lighting. The town behind the title,
// sign-in, the creator and the towns list is lit for the player's local time, but at dusk and night the clock alone left it murky (a mauve
// plaza on dark green under the panels). Here the lights on the town never fall below a cosy evening: the hemisphere and sun keep to a
// floor, a low sun is raised enough to light the plaza, and the dimmer the clock's light the more its colours lean to a warm fill. The
// evening sky (and so the fog), the lit windows, the lamps and the water keep the clock's values. The town itself (engine/lighting.ts in
// the Game) is never graded. Pure: it returns new values and leaves the clock's alone.
import { tuning } from '@whiskerton/content';
import * as THREE from 'three';
import type { LightValues } from '../engine/lighting';

const G = tuning.title.grade;
const FILL = { sky: new THREE.Color(G.fillSky), ground: new THREE.Color(G.fillGround), sun: new THREE.Color(G.fillSun) };

export function titleGrade(l: LightValues): LightValues {
  // How far the floor has to lift the hemisphere: 0 in daylight, more the darker the hour (dusk ~0.3, night ~0.5).
  const lift = Math.max(0, 1 - l.hemiIntensity / G.hemiFloor);
  const t = Math.min(1, lift * 2) * G.warmth;
  const sunDir = l.sunDir.clone();
  if (lift > 0 && sunDir.y < G.sunMinY) {
    const flat = Math.hypot(sunDir.x, sunDir.z) || 1, k = Math.sqrt(1 - G.sunMinY * G.sunMinY) / flat;
    sunDir.set(sunDir.x * k, G.sunMinY, sunDir.z * k);
  }
  return {
    ...l,
    sky: l.sky.clone(), horizon: l.horizon.clone(), water: l.water.clone(), sunDir,
    hemiSky: l.hemiSky.clone().lerp(FILL.sky, t),
    hemiGround: l.hemiGround.clone().lerp(FILL.ground, t),
    sun: l.sun.clone().lerp(FILL.sun, t),
    hemiIntensity: Math.max(l.hemiIntensity, G.hemiFloor),
    sunIntensity: lift > 0 ? Math.max(l.sunIntensity, G.sunFloor) : l.sunIntensity,
  };
}
